import { useState, useMemo } from 'react'
import { addDays, format } from 'date-fns'
import dayjs from 'dayjs'
import { formatBigMoney } from '@utils/formatters'
import { timeDisplay } from '../../components/Freight/shared/util/timeDisplay'
import { useInvoices } from './useInvoices'
import {
  type BookType,
  type Invoice,
  type InvoiceTransaction,
  INVOICE_STATUS,
  INVOICE_TYPE,
  DETAIL_TYPE,
} from './types'
import type { DashboardMetrics, DailyThroughput } from './types/dashboard'

// Define ProcessedInvoice type combining Invoice and specific group properties
type ProcessedInvoice = Invoice & {
  invoice_group_id: string
  book_type: BookType
  group_number: string
  counterparty_name?: string
  counterparty_id?: string
  created_by?: string
  updated_by?: string
  created_date?: string
}

/**
 * Interface for detail totals in customer aggregation
 */
interface DetailTotals {
  baseFreight: number
  accessorials: number
  surcharge: number
  accessorialsByType: { [key: string]: number }
}

/**
 * Calculate top accessorials from invoice data
 */
const calculateTopAccessorials = (invoices: ProcessedInvoice[]) => {
  const accessorialTotals: { [key: string]: number } = {}

  for (const inv of invoices) {
    // Access transactions directly from the invoice
    for (const trans of inv.transactions || []) {
      for (const detail of trans.details || []) {
        if (detail.type === DETAIL_TYPE.ACCESSORIAL) {
          const subtype = detail.subtype || 'Other'
          accessorialTotals[subtype] = (accessorialTotals[subtype] || 0) + (detail.total_override || detail.total || 0)
        }
      }
    }
  }

  return Object.entries(accessorialTotals)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([name, value]) => ({
      name,
      value: formatBigMoney(value),
    }))
}

/**
 * Aggregate customer totals from invoice data
 */
const aggregateCustomerTotals = (invoices: ProcessedInvoice[]): { [key: string]: DetailTotals } => {
  const customerTotals: { [key: string]: DetailTotals } = {}

  for (const inv of invoices) {
    const customerId = inv.counterparty_name || 'Unknown' // Fallback to "Unknown" if name is missing

    if (!customerTotals[customerId]) {
      customerTotals[customerId] = {
        baseFreight: 0,
        accessorials: 0,
        surcharge: 0,
        accessorialsByType: {},
      }
    }

    // Iterate through transactions directly from the invoice
    for (const trans of inv.transactions || []) {
      for (const detail of trans.details || []) {
        if (detail.type === DETAIL_TYPE.BASE_FREIGHT) {
          customerTotals[customerId].baseFreight += detail.total_override || detail.total || 0
        } else if (detail.type === DETAIL_TYPE.ACCESSORIAL) {
          const accessorialAmount = detail.total_override || detail.total || 0
          customerTotals[customerId].accessorials += accessorialAmount
          const subtype = detail.subtype || 'Other'
          customerTotals[customerId].accessorialsByType[subtype] =
            (customerTotals[customerId].accessorialsByType[subtype] || 0) + accessorialAmount
        } else if (detail.type === DETAIL_TYPE.SURCHARGE) {
          customerTotals[customerId].surcharge += detail.total_override || detail.total || 0
        }
      }
    }
  }

  return customerTotals
}

/**
 * Count errors or warnings in an invoice
 */
const countErrorsOrWarnings = (inv: ProcessedInvoice, isError: boolean) => {
  if ((isError && inv.status !== INVOICE_STATUS.BLOCKED) || (!isError && inv.status === INVOICE_STATUS.BLOCKED)) {
    return 0
  }

  return inv.transactions.filter((trans) =>
    trans.details.some(
      (detail) => detail.status === INVOICE_STATUS.NEEDS_APPROVAL || detail.status === INVOICE_STATUS.ERROR
    )
  ).length
}

/**
 * Count detail errors in a transaction
 */
const countDetailErrors = (trans: InvoiceTransaction, shouldCount: boolean) => {
  if (!shouldCount) return 0

  return trans.details.filter(
    (detail) => detail.status === INVOICE_STATUS.ERROR || detail.status === INVOICE_STATUS.NEEDS_APPROVAL
  ).length
}

/**
 * Process customer data from an invoice
 */
const getCustomerData = (
  inv: ProcessedInvoice, // Use ProcessedInvoice type
  customer: DashboardMetrics['customers'][0]
) => {
  const invoiceTransactions = inv.transactions || []

  // Update customer stats
  customer.orderCount += invoiceTransactions.length
  if (inv.status === INVOICE_STATUS.SENT) {
    customer.sentMTD += inv.total_amount || 0 // Handle potential null total_amount
    customer.sentOrders += invoiceTransactions.length
  } else {
    customer.openAmount += inv.total_amount || 0 // Handle potential null total_amount
    customer.openOrders += invoiceTransactions.length
    customer.errorCounts += countErrorsOrWarnings(inv, true)
    customer.warningCounts += countErrorsOrWarnings(inv, false)
  }

  // Check for earlier invoice date
  if (
    inv.sent_date && // Use sent_date if it exists and is relevant here
    dayjs(inv.sent_date).isBefore(dayjs(customer.nextInvoiceDate))
  ) {
    customer.nextInvoiceDate = inv.sent_date
  } else if (
    inv.when_to_send_date && // Fallback to end_date
    dayjs(inv.when_to_send_date).isBefore(dayjs(customer.nextInvoiceDate))
  ) {
    customer.nextInvoiceDate = inv.when_to_send_date
  }

  // Add transaction details as orders
  for (const trans of invoiceTransactions) {
    const errorCount = countDetailErrors(trans, inv.status === INVOICE_STATUS.BLOCKED)
    const warningCount = countDetailErrors(trans, inv.status !== INVOICE_STATUS.BLOCKED)

    customer.orders.push({
      ...trans,
      invoiceNumber: inv.invoice_number || '', // Ensure string
      invoiceGroupId: inv.invoice_group_id, // Use group ID from ProcessedInvoice
      orderNumber: String(trans.order_number), // Ensure string
      orderId: trans.order_id, // Use camelCase ID
      dateTime: inv.end_date || '', // Ensure string, use appropriate date field
      invoiceStatus: inv.status,
      destinations: trans.destinations || [], // Ensure array
      miles: trans.distance || 0,
      gallons: trans.volume || 0,
      total: trans.total || 0, // Ensure number
      errorCount,
      warningCount,
    })
  }

  return customer
}

/**
 * Get rebill activity data from an invoice
 */
const getRebillGoodies = (inv: ProcessedInvoice) => {
  // Check if inv.invoices exists and has elements before trying to find
  if (inv.status === INVOICE_STATUS.CR_APPROVAL && Array.isArray(inv.invoices) && inv.invoices.length > 0) {
    // find the original invoice
    const originalInvoice = inv.invoices.find((i) => i.type === INVOICE_TYPE.ORIGINAL)

    // Ensure originalInvoice and its total_amount are defined
    const originalTotalAmount = originalInvoice?.total_amount ?? 0

    return [
      {
        id: `${inv.id}-credit`,
        type: 'credit' as const,
        user: inv.updated_by || 'System', // Use updated_by from ProcessedInvoice
        invoiceId: inv.invoice_number || '', // Ensure string
        amount: (inv.total_amount ?? 0) - originalTotalAmount, // Ensure inv.total_amount is defined
        time: timeDisplay(dayjs.utc(inv.start_date).local().format()).display, // Use appropriate date
        status: inv.status,
      },
    ]
  }
  return []
}

/**
 * Hook for dashboard data with metrics and invoice information
 */
export const useDashboardData = ({
  costRevenue,
}: {
  costRevenue: BookType
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const { useInvoicesQuery } = useInvoices()
  // Get current date range
  const today = addDays(new Date(), 0)
  // Calculate start date as 30 days ago
  const startDate30DaysAgo = dayjs(today).subtract(30, 'day').toDate()
  const [dateRange, setDateRange] = useState<{
    start_date: Date
    end_date: Date
  }>({
    start_date: startDate30DaysAgo, // Use past 30 days
    end_date: today,
  })

  const { data: invoiceData, isLoading: isQueryLoading } = useInvoicesQuery({
    start_date: dateRange.start_date,
    end_date: dateRange.end_date,
  })

  // Use useMemo to transform invoice data into dashboard metrics
  const dashboardData = useMemo<DashboardMetrics | null>(() => {
    if (!invoiceData) return null

    // Start processing the invoice data
    const invoiceGroups = invoiceData.rows || []
    const processedInvoices: ProcessedInvoice[] = invoiceGroups
      .filter((group) => group.book_type === costRevenue)
      .map((group) => {
        // grab the last rebill invoice if exists, otherwise grab the last original invoice
        const rebillInvoices = (group.invoices || []).filter((inv) => inv.type === INVOICE_TYPE.REBILL)

        const originalInvoices = (group.invoices || []).filter((inv) => inv.type === INVOICE_TYPE.ORIGINAL)

        // Use optional chaining and nullish coalescing for safer access
        const latestRebill = rebillInvoices
          ?.sort((a, b) => dayjs(b.end_date).diff(dayjs(a.end_date))) // Use end_date or appropriate date
          ?.at(0)

        const latestOriginal = originalInvoices
          ?.sort((a, b) => dayjs(b.end_date).diff(dayjs(a.end_date))) // Use end_date or appropriate date
          ?.at(0)

        // Choose the latest invoice, prioritizing rebill
        const latestInvoice = latestRebill ?? latestOriginal

        // Return null if latestInvoice is undefined to filter out later
        if (!latestInvoice) {
          return null
        }

        return {
          // Start with group properties, potentially overwritten by invoice properties if names clash
          // but ensure unique group props are kept.
          invoice_group_id: group.id, // Use group.id for the group identifier
          book_type: group.book_type,
          group_number: group.group_number,
          // Explicitly keep group counterparty_name as it seems more reliable
          created_by: group.created_by,
          updated_by: group.updated_by,
          created_date: group.created_date,

          // Invoice properties (spread latestInvoice safely)
          ...latestInvoice,

          // Explicitly keep group counterparty_name *after* spread to ensure it overwrites
          counterparty_name: group.counterparty_name,
          // Explicitly set invoice status as it might differ from group status
          status: latestInvoice.status,
        } as ProcessedInvoice // Cast the final object shape
      })
      // Filter out any nulls from the map operation
      .filter((inv): inv is ProcessedInvoice => inv !== null)

    const allOrders = processedInvoices.flatMap((inv) => inv.transactions || [])

    // Calculate days between start_date and end_date
    const dayDiff = Math.ceil((dateRange.end_date.getTime() - dateRange.start_date.getTime()) / (1000 * 60 * 60 * 24))

    // Generate daily throughput data
    const dailyThroughput: DailyThroughput[] = Array.from({ length: dayDiff + 1 }, (_, i) => {
      const date = addDays(dateRange.start_date, i)
      const dateStr = format(date, 'MMM d')
      const currentDay = dayjs(date)

      // 1. Calculate SENT amount for the day
      // Find invoices sent on the current day
      const sentInvoicesToday = processedInvoices.filter((inv) =>
        inv.sent_date ? dayjs(inv.sent_date).isSame(currentDay, 'day') : false
      )
      // Get transactions from those invoices and filter for POSTED status
      const sentOrdersToday = sentInvoicesToday
        .flatMap((inv) => inv.transactions || [])
        .filter((order) => order?.status === INVOICE_STATUS.POSTED)
      // Sum the totals
      const sentAmount = sentOrdersToday.reduce((sum, order) => sum + (order?.total ?? 0), 0)

      // 2. Calculate OPEN amount for the day
      // Find all transactions with a date matching currentDay and status != POSTED
      const openOrdersToday = allOrders.filter((order) => {
        const isCorrectDay = order?.date && dayjs(order.date).isSame(currentDay, 'day')
        const isOpenStatus = order?.status && order.status !== INVOICE_STATUS.POSTED
        return isCorrectDay && isOpenStatus
      })
      // Sum the totals
      const openAmount = openOrdersToday.reduce((sum, order) => sum + (order?.total ?? 0), 0)

      return {
        date: dateStr,
        fullDate: date,
        sent: sentAmount,
        open: openAmount,
        ready: 0,
      }
    })

    // Process customer data
    const customers = processedInvoices.reduce(
      (acc, inv) => {
        // Find customer by counterparty_id from the group/processed invoice
        // Ensure counterparty_id exists and is a string for reliable lookup
        const lookupId = inv.counterparty_id ? String(inv.counterparty_id) : inv.counterparty_name
        let customer = acc.find((c) => c.id === lookupId)

        if (!customer) {
          customer = {
            // Use the same lookupId for the new customer's ID
            id: lookupId,
            name: inv.counterparty_name,
            orderCount: 0,
            sentOrders: 0,
            openOrders: 0,
            sentMTD: 0,
            openAmount: 0,
            warningCounts: 0,
            errorCounts: 0,
            nextInvoiceDate: inv.end_date || new Date().toISOString(), // Use appropriate date, provide default
            orders: [],
          }
          acc.push(customer)
        }

        // Pass the correctly typed invoice to getCustomerData
        getCustomerData(inv, customer)

        return acc
      },
      [] as DashboardMetrics['customers']
    )

    // Calculate metrics data
    const { metrics, mtdSummary, invoiceMetrics } = generateMetricsData(processedInvoices, allOrders)

    // Process activity data
    const recentActivity = processedInvoices
      ?.flatMap((inv) => [
        ...(inv.comments || []).map((comment) => ({
          id: comment.id,
          type: 'comment' as const, // Explicit type
          user: comment.username,
          invoiceId: inv.invoice_number || '', // Ensure string
          message: comment.text,
          time: timeDisplay(dayjs.utc(comment.created_on).local().format()).display,
          status: inv.status, // Use status from ProcessedInvoice
        })),
        ...getRebillGoodies(inv), // Pass ProcessedInvoice
      ])
      // Ensure time property exists and is comparable before sorting
      .filter((item) => item && typeof item.time === 'string')
      .sort((a, b) => b.time.localeCompare(a.time))
      .slice(0, 20)

    // Process attention items
    const noRateFoundOrders = processedInvoices.flatMap((inv) => {
      return (inv.transactions || []).filter((trans) =>
        (trans.details || []).some((detail) => detail.status === INVOICE_STATUS.ERROR)
      )
    })
    const needsApprovalOrders = processedInvoices.flatMap((inv) => {
      return (inv.transactions || []).filter((trans) =>
        (trans.details || []).some((detail) => detail.status === INVOICE_STATUS.NEEDS_APPROVAL)
      )
    })

    const needsAttentionDetails = needsApprovalOrders.flatMap((trans) =>
      (trans.details || []).filter((detail) => detail.status === INVOICE_STATUS.NEEDS_APPROVAL)
    )
    const attentionItems = {
      noRateFound: noRateFoundOrders,
      needsApprovalOrders,
      accessorialApprovals: needsAttentionDetails,
    }

    // Return compiled dashboard data
    return {
      dailyThroughput,
      mtdSummary,
      invoiceMetrics,
      metrics,
      customers,
      recentActivity,
      attentionItems,
    }
  }, [invoiceData, costRevenue, dateRange])

  // Derive final loading state
  const isDataLoading = isQueryLoading || !dashboardData

  return {
    data: dashboardData,
    isLoading: isDataLoading,
    dateRange,
    setDateRange,
  }
}

const generateMetricsData = (invoices: ProcessedInvoice[], allOrders: InvoiceTransaction[]) => {
  // Calculate totals from customer data
  const customerTotals = aggregateCustomerTotals(invoices)
  const topAccessorials = calculateTopAccessorials(invoices)

  // Calculate total amounts
  const totalAmount = Object.values(customerTotals).reduce(
    (sum, totals) => sum + totals.baseFreight + totals.accessorials + totals.surcharge,
    0
  )
  const baseFreightTotal = Object.values(customerTotals).reduce((sum, totals) => sum + totals.baseFreight, 0)
  const accessorialTotal = Object.values(customerTotals).reduce((sum, totals) => sum + totals.accessorials, 0)
  const surchargeTotal = Object.values(customerTotals).reduce((sum, totals) => sum + totals.surcharge, 0)

  // Generate MTD summary
  const mtdSummary = {
    sent: {
      amount: formatBigMoney(
        allOrders.filter((order) => order.status === INVOICE_STATUS.POSTED).reduce((sum, order) => sum + order.total, 0)
      ),
      orderCount: allOrders.filter((order) => order.status === INVOICE_STATUS.POSTED).length,
      invoiceCount: invoices.filter((inv) => inv.status === INVOICE_STATUS.SENT).length,
    },
    open: {
      amount: formatBigMoney(
        allOrders.filter((order) => order.status !== INVOICE_STATUS.POSTED).reduce((sum, order) => sum + order.total, 0)
      ),
      orderCount: allOrders.filter((order) => order.status !== INVOICE_STATUS.POSTED).length,
      invoiceCount: invoices.filter((inv) => inv.status !== INVOICE_STATUS.SENT).length,
    },
  }

  // Generate invoice metrics for blocked and credit rebills
  const blockedInvoices = invoices.filter((inv) => inv.status === INVOICE_STATUS.BLOCKED)
  const oldestBlockedInvoice = blockedInvoices
    .sort((a, b) => dayjs(a.when_to_send_date).diff(dayjs(b.when_to_send_date)))
    .at(0)
  const creditRebills = invoices.filter((inv) => inv.status === INVOICE_STATUS.CR_APPROVAL)
  const oldestCreditRebill = creditRebills.sort((a, b) => dayjs(a.end_date).diff(dayjs(b.end_date))).at(0)

  const invoiceMetrics = {
    blocked: {
      count: blockedInvoices.length,
      trend: '25%',
      trendDirection: 'up' as const,
      oldestTime:
        (oldestBlockedInvoice?.when_to_send_date && timeDisplay(oldestBlockedInvoice.when_to_send_date).display) ||
        'Unknown',
    },
    creditRebills: {
      count: creditRebills.length,
      trend: '25%',
      trendDirection: 'up' as const,
      openForApproval: creditRebills.length,
      oldestTime:
        (oldestCreditRebill?.when_to_send_date && timeDisplay(oldestCreditRebill.when_to_send_date).display) || '',
    },
  }

  // Generate metrics summaries with percentages and top items
  const metrics = {
    baseFreight: {
      total: formatBigMoney(baseFreightTotal),
      percentage: `${totalAmount > 0 ? ((baseFreightTotal / totalAmount) * 100).toFixed(0) : '0'}%`,
      topCustomers: Object.entries(customerTotals)
        .sort(([, a], [, b]) => b.baseFreight - a.baseFreight)
        .slice(0, 3)
        .filter(([, totals]) => totals.baseFreight > 0)
        .map(([name, totals]) => ({
          name,
          value: formatBigMoney(totals.baseFreight),
        })),
    },
    accessorials: {
      total: formatBigMoney(accessorialTotal),
      percentage: `${totalAmount > 0 ? ((accessorialTotal / totalAmount) * 100).toFixed(0) : '0'}%`,
      topTypes: topAccessorials,
    },
    surcharge: {
      total: formatBigMoney(surchargeTotal),
      percentage: `${totalAmount > 0 ? ((surchargeTotal / totalAmount) * 100).toFixed(0) : '0'}%`,
      topCustomers: Object.entries(customerTotals)
        .sort(([, a], [, b]) => b.surcharge - a.surcharge)
        .slice(0, 3)
        .filter(([, totals]) => totals.surcharge > 0)
        .map(([name, totals]) => ({
          name,
          value: formatBigMoney(totals.surcharge),
        })),
    },
  }

  return { metrics, mtdSummary, invoiceMetrics }
}
